<template>
  <div class="space-y-6">
    <div v-if="!showEditor && !showCreate" class="space-y-4">
      <section class="app-section">
        <div class="app-toolbar project-toolbar">
          <div class="toolbar-heading">
            <h2 class="m-0 text-xl font-semibold">{{ presentationsTitle }}</h2>
            <p class="toolbar-subtitle">Всего: {{ isLoading ? '...' : articles.length }}</p>
          </div>
          <button class="button" @click="startCreate">Новая презентация</button>
        </div>

        <div v-if="isLoading" class="state-box">
          <p>Загрузка презентаций...</p>
        </div>
        <div v-else-if="articles.length === 0" class="state-box">
          <p>Нет презентаций</p>
        </div>
        <div v-else class="projects-grid">
          <ProjectCard
            v-for="article in articles"
            :key="article.id"
            class="article-project-card"
            :project="articleToProjectCard(article)"
            :showProject="openEditorById"
            :show-open-label="false"
            :uniform-height="true"
          >
            <template #actions>
              <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click.stop="openEditor(article)">
                Редактировать
              </button>
              <button class="button button-danger !h-8 !px-3 !py-0 text-xs" @click.stop="remove(article)">
                Удалить
              </button>
            </template>
          </ProjectCard>
        </div>
      </section>
    </div>

    <!-- Форма создания статьи -->
    <div v-if="showCreate" class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-bold text-gray-900 dark:text-primary">Создание презентации</h2>
        <button class="px-4 py-2 rounded bg-gray-200 dark:bg-secondary-bg hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-primary transition-colors" @click="showCreate = false">← Назад к списку</button>
      </div>
      
      <div class="p-6 rounded bg-gray-100 dark:bg-secondary-bg space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm mb-1 text-gray-900 dark:text-primary">Заголовок *</label>
            <input
              v-model="newForm.title" 
              class="w-full bg-white dark:bg-background p-2 rounded text-gray-900 dark:text-primary border border-gray-300 dark:border-gray-700" 
              placeholder="Введите заголовок презентации"
              @input="generateSlug"
            />
          </div>
          <div>
            <label class="block text-sm mb-1 text-gray-900 dark:text-primary">Slug *</label>
            <input 
              v-model="newForm.slug" 
              class="w-full bg-white dark:bg-background p-2 rounded text-gray-900 dark:text-primary border border-gray-300 dark:border-gray-700" 
              placeholder="about-us"
            />
          </div>
        </div>
        <div>
          <label class="block text-sm mb-1 text-gray-900 dark:text-primary">Короткое описание</label>
          <textarea 
            v-model="newForm.description" 
            class="w-full bg-white dark:bg-background p-2 rounded min-h-[96px] text-gray-900 dark:text-primary border border-gray-300 dark:border-gray-700" 
            placeholder="Короткое описание статьи"
          />
        </div>
        <div>
          <label class="flex items-center space-x-2">
            <input type="checkbox" v-model="newForm.visible" />
            <span class="text-gray-900 dark:text-primary">Презентация видна на сайте</span>
          </label>
        </div>
        <div class="pt-4 border-t border-gray-300 dark:border-gray-700 flex items-center justify-end space-x-2">
          <button class="px-4 py-2 rounded bg-gray-200 dark:bg-secondary-bg hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-primary transition-colors" @click="showCreate = false">Отмена</button>
          <button class="px-4 py-2 rounded bg-accent text-white hover:bg-blue-600 transition-colors" @click="create">Создать презентацию</button>
        </div>
      </div>
    </div>

    <!-- Редактор статьи -->
    <div v-if="showEditor && currentId" class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-bold text-gray-900 dark:text-primary">Редактирование презентации</h2>
        <button class="px-4 py-2 rounded bg-gray-200 dark:bg-secondary-bg hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-primary transition-colors" @click="closeEditor">← Назад к списку</button>
      </div>
      <BlogEditor :article-id="currentId" />
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { fetchBlogArticles, fetchMyBlogArticles, createBlogArticle, deleteBlogArticle } from '../../services/api';
import BlogEditor from './BlogEditor.vue';
import ProjectCard from '../ProjectCard.vue';
import { isUserAdminForUi } from '../../utils/user-role-override';

export default {
  name: 'BlogView',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    BlogEditor,
    ProjectCard
  },
  computed: {
    canManageAll() {
      return Boolean(this.currentUser?.is_superuser) || isUserAdminForUi(this.currentUser);
    },
    presentationsTitle() {
      return this.canManageAll ? 'Презентации' : 'Мои презентации';
    }
  },
  data() {
    return {
      articles: [],
      isLoading: false,
      showEditor: false,
      currentId: null,
      showCreate: false,
      newForm: { slug: '', title: '', description: '', visible: true }
    }
  },
  created() {
    this.load();
  },
  watch: {
    currentUser: {
      deep: true,
      handler() {
        this.load();
      }
    }
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;
        const response = this.canManageAll ? await fetchBlogArticles() : await fetchMyBlogArticles();
        this.articles = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error('Ошибка при получении статей V2:', error);
        const toast = useToast();
        toast.error('Не удалось загрузить презентации');
        this.articles = [];
      } finally {
        this.isLoading = false;
      }
    },
    articleToProjectCard(article) {
      const slug = String(article?.slug || '').trim();
      return {
        id: article.id,
        title: article.title || 'Без названия',
        category: slug ? `/${slug}` : 'V2',
        content: article.description || '',
        visible: article.visible !== false
      };
    },
    openEditorById(articleId) {
      if (!articleId) return;
      this.currentId = articleId;
      this.showEditor = true;
    },
    openEditor(article) {
      this.currentId = article.id;
      this.showEditor = true;
    },
    closeEditor() {
      this.showEditor = false;
      this.currentId = null;
      this.load();
    },
    startCreate() {
      this.showCreate = true;
      this.newForm = { slug: '', title: '', description: '', visible: true };
    },
    generateSlug() {
      if (!this.newForm.title) return;
      
      // Транслитерация и генерация slug
      const slug = this.transliterate(this.newForm.title)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // убираем все кроме букв, цифр, пробелов и дефисов
        .replace(/\s+/g, '-') // заменяем пробелы на дефисы
        .replace(/-+/g, '-') // убираем множественные дефисы
        .replace(/^-|-$/g, ''); // убираем дефисы в начале и конце
      
      this.newForm.slug = slug;
    },
    transliterate(text) {
      const map = {
        'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
        'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
        'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
        'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
        'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
        'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'E',
        'Ж': 'Zh', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
        'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
        'Ф': 'F', 'Х': 'H', 'Ц': 'Ts', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Sch',
        'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya'
      };
      
      return text.split('').map(char => map[char] || char).join('');
    },
    async create() {
      if (!this.newForm.slug || !this.newForm.title) return;
      const payload = {
        slug: this.newForm.slug,
        title: this.newForm.title,
        description: this.newForm.description || null,
        visible: !!this.newForm.visible,
      };
      await createBlogArticle(payload);
      const toast = useToast();
      toast.success('Презентация создана');
      this.showCreate = false;
      this.newForm = { slug: '', title: '', description: '', visible: true };
      await this.load();
    },
    async remove(article) {
      await deleteBlogArticle(article.id);
      const toast = useToast();
      toast.success('Презентация удалена');
      await this.load();
    }
  }
}
</script>

<style scoped>
.project-toolbar {
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.toolbar-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toolbar-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: hsl(var(--muted-foreground));
}

.projects-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  align-items: stretch;
}

.state-box {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  text-align: center;
  padding: 16px;
  font-size: 0.95rem;
}

@media (max-width: 720px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
